/*
 * /join/:slug — the brand's public "Join the mailing list" page (design
 * MY-DESIGN-BRAND-SIGNUP-AND-COMPLETE-PROFILE-1020 §8 Channel 1, which
 * supersedes §2.1–2.4; row _design/DESIGN-BRAND-SIGNUP-AND-NEWSLETTERS-V1).
 * Public (matched before the auth gate in App.tsx), behind
 * VITE_BRAND_SIGNUP_ENABLED.
 *
 * ONE quick step, deliberately a light newsletter signup, not an account
 * signup: email, first name, optional last name, the brand box (UNTICKED,
 * with the wording from GET /join/{slug}/config), the "I'm 18 or over"
 * attestation, a hidden honeypot and Cloudflare Turnstile. No city, no gym,
 * no password.
 *
 * PRIVACY: a submit that passes validation ALWAYS shows the same "Check your
 * email to confirm" — never whether the address is known. The email is not
 * echoed back.
 */
import { useEffect, useState, type CSSProperties, type FormEvent } from 'react'
import { Brand, Card } from '../components/ui'
import { Turnstile } from '../components/Turnstile'
import { fetchJoinConfig, myApiBase, MyApiError, submitJoinSignup, type JoinConfig } from '../data/myApi'
import { turnstileSiteKey } from '../lib/brandSignupFlag'
import { useLocale, type MessageKey } from '../i18n/useLocale'
import { EmailHiddenCharNotice, useCleanEmail } from '../components/EmailHiddenCharNotice'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const NAME_MAX = 100

const labelStyle: CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }
const inputStyle: CSSProperties = {
  width: '100%', minHeight: 44, border: '1px solid var(--hairline-14)', borderRadius: 10, padding: '0 12px',
  fontSize: 15, background: 'var(--bg-frame)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const checkRowStyle: CSSProperties = { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.45, cursor: 'pointer' }
const checkboxStyle: CSSProperties = { width: 18, height: 18, marginTop: 1, flex: 'none', accentColor: 'var(--accent)' }
const errStyle: CSSProperties = { fontSize: 12, color: 'var(--warn)', margin: '4px 0 0' }
/** Off-screen honeypot: present for bots, invisible and unreachable for people. */
const honeypotStyle: CSSProperties = { position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }

type Phase =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'not_found' }
  | { kind: 'form'; config: JoinConfig }
  | { kind: 'sent'; config: JoinConfig }

/** Submit error code → message key (one message per code). */
function submitErrorKey(e: unknown): MessageKey {
  if (!(e instanceof MyApiError)) return 'join.error'
  if (e.status === 422 || e.code === 'adult_attestation_required') return 'join.adult.required'
  if (e.status === 403 || e.code === 'turnstile_failed') return 'join.turnstileFailed'
  if (e.status === 404) return 'join.notFound'
  if (e.code === 'invalid_email') return 'join.email.invalid'
  if (e.status === 503) return 'join.unavailable'
  return 'join.error'
}

export function JoinSignup({ slug, fetchImpl }: { slug: string; fetchImpl?: typeof fetch }) {
  const { t } = useLocale()
  const siteKey = turnstileSiteKey()
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })

  useEffect(() => {
    const base = myApiBase()
    if (!base) {
      setPhase({ kind: 'unavailable' })
      return
    }
    let cancelled = false
    fetchJoinConfig(base, slug, fetchImpl ?? fetch)
      .then((config) => { if (!cancelled) setPhase({ kind: 'form', config }) })
      .catch((e) => {
        if (cancelled) return
        setPhase(e instanceof MyApiError && e.status === 404 ? { kind: 'not_found' } : { kind: 'unavailable' })
      })
    return () => { cancelled = true }
  }, [slug, fetchImpl])

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 16, boxSizing: 'border-box' }}>
      <div className="col" data-testid="join-page" style={{ width: '100%', maxWidth: 400, gap: 16 }}>
        <div className="col" style={{ alignItems: 'center' }}>
          <Brand size={24} />
        </div>
        {phase.kind === 'loading' && (
          <p role="status" className="secondary" style={{ fontSize: 13, textAlign: 'center' }}>{t('join.loading')}</p>
        )}
        {phase.kind === 'unavailable' && (
          <Card><p data-testid="join-unavailable" role="status" className="secondary" style={{ fontSize: 13, textAlign: 'center', padding: 18, margin: 0 }}>{t('join.unavailable')}</p></Card>
        )}
        {phase.kind === 'not_found' && (
          <Card><p data-testid="join-not-found" role="alert" className="secondary" style={{ fontSize: 13, textAlign: 'center', padding: 18, margin: 0 }}>{t('join.notFound')}</p></Card>
        )}
        {phase.kind === 'sent' && (
          <Card>
            <div data-testid="join-sent" role="status" className="col" style={{ padding: 20, gap: 8, textAlign: 'center' }}>
              <h1 style={{ fontSize: 18, margin: 0 }}>{t('join.sent.title')}</h1>
              <p className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('join.sent.body')}</p>
            </div>
          </Card>
        )}
        {phase.kind === 'form' && (
          <JoinForm
            slug={slug}
            config={phase.config}
            siteKey={siteKey}
            fetchImpl={fetchImpl}
            onSent={() => setPhase({ kind: 'sent', config: phase.config })}
            onNotFound={() => setPhase({ kind: 'not_found' })}
          />
        )}
      </div>
    </div>
  )
}

function JoinForm({ slug, config, siteKey, fetchImpl, onSent, onNotFound }: {
  slug: string
  config: JoinConfig
  siteKey: string | null
  fetchImpl?: typeof fetch
  onSent: () => void
  onNotFound: () => void
}) {
  const { t } = useLocale()
  const brand = config.display_name
  const [email, setEmail, emailHidden] = useCleanEmail()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  // UNTICKED by default — the brand box is the person's own choice.
  const [optIn, setOptIn] = useState(false)
  const [adult, setAdult] = useState(false)
  const [hp, setHp] = useState('')
  const [token, setToken] = useState<string | null>(null)
  const [resetKey, setResetKey] = useState(0)
  const [busy, setBusy] = useState(false)
  const [tried, setTried] = useState(false)
  const [error, setError] = useState<MessageKey | null>(null)

  const emailOk = EMAIL_RE.test(email.trim())
  const firstOk = firstName.trim().length > 0
  const ready = siteKey !== null

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setTried(true)
    setError(null)
    if (!ready || busy) return
    if (!emailOk || !firstOk || !adult) return
    if (!token) {
      setError('join.turnstileFailed')
      return
    }
    const base = myApiBase()
    if (!base) {
      setError('join.unavailable')
      return
    }
    setBusy(true)
    try {
      await submitJoinSignup(base, slug, {
        email: email.trim(),
        first_name: firstName.trim(),
        ...(lastName.trim() ? { last_name: lastName.trim() } : {}),
        opt_in: optIn,
        adult_attested: adult,
        turnstile_token: token,
        hp,
      }, fetchImpl ?? fetch)
      onSent()
    } catch (err) {
      if (err instanceof MyApiError && err.status === 404) {
        onNotFound()
        return
      }
      setError(submitErrorKey(err))
      // A Turnstile token is single-use: always fetch a fresh challenge.
      setToken(null)
      setResetKey((k) => k + 1)
      setBusy(false)
    }
  }

  return (
    <Card>
      <form data-testid="join-form" noValidate onSubmit={(e) => void onSubmit(e)} className="col" style={{ padding: 18, gap: 14, position: 'relative' }}>
        <div className="col" style={{ gap: 4, textAlign: 'center' }}>
          <h1 data-testid="join-title" style={{ fontSize: 18, margin: 0 }}>{t('join.title', { brand })}</h1>
          <p className="secondary" style={{ fontSize: 13, margin: 0 }}>{t('join.intro', { brand })}</p>
        </div>

        {!ready && (
          <p data-testid="join-unavailable" role="status" style={{ fontSize: 12.5, color: 'var(--warn)', margin: 0, textAlign: 'center' }}>{t('join.unavailable')}</p>
        )}

        <div>
          <label htmlFor="join-email" style={labelStyle}>{t('join.email.label')}</label>
          <input
            id="join-email" data-testid="join-email" type="email" autoComplete="email" inputMode="email"
            value={email} maxLength={254} onChange={(e) => setEmail(e.target.value)} style={inputStyle}
            aria-invalid={tried && !emailOk} aria-describedby={tried && !emailOk ? 'join-email-err' : undefined}
          />
          <EmailHiddenCharNotice show={emailHidden} testid="join-email-hidden-char" />
          {tried && !emailOk && <p id="join-email-err" data-testid="join-email-error" style={errStyle}>{t('join.email.invalid')}</p>}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 140px' }}>
            <label htmlFor="join-first" style={labelStyle}>{t('join.firstName.label')}</label>
            <input
              id="join-first" data-testid="join-first-name" autoComplete="given-name" value={firstName}
              maxLength={NAME_MAX} onChange={(e) => setFirstName(e.target.value)} style={inputStyle}
              aria-invalid={tried && !firstOk} aria-describedby={tried && !firstOk ? 'join-first-err' : undefined}
            />
            {tried && !firstOk && <p id="join-first-err" data-testid="join-first-name-error" style={errStyle}>{t('join.firstName.required')}</p>}
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label htmlFor="join-last" style={labelStyle}>{t('join.lastName.label')}</label>
            <input
              id="join-last" data-testid="join-last-name" autoComplete="family-name" value={lastName}
              maxLength={NAME_MAX} onChange={(e) => setLastName(e.target.value)} style={inputStyle}
            />
          </div>
        </div>

        {/* Honeypot: hidden from people and assistive tech; a bot fills it. */}
        <div aria-hidden="true" style={honeypotStyle}>
          <label htmlFor="join-website">Website</label>
          <input id="join-website" data-testid="join-hp" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
        </div>

        <label style={checkRowStyle}>
          <input type="checkbox" data-testid="join-optin" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} style={checkboxStyle} />
          <span data-testid="join-optin-wording">{config.opt_in_wording}</span>
        </label>
        <div>
          <label style={checkRowStyle}>
            <input
              type="checkbox" data-testid="join-adult" checked={adult} onChange={(e) => setAdult(e.target.checked)} style={checkboxStyle}
              aria-invalid={tried && !adult} aria-describedby={tried && !adult ? 'join-adult-err' : undefined}
            />
            <span>{t('join.adult.label')}</span>
          </label>
          {tried && !adult && <p id="join-adult-err" data-testid="join-adult-error" style={errStyle}>{t('join.adult.required')}</p>}
        </div>

        {siteKey && (
          <Turnstile
            siteKey={siteKey}
            resetKey={resetKey}
            label={t('join.humanCheck')}
            onToken={setToken}
            onError={() => setToken(null)}
          />
        )}

        {error && <p data-testid="join-error" role="alert" style={{ ...errStyle, margin: 0, textAlign: 'center' }}>{t(error)}</p>}

        <button
          type="submit"
          data-testid="join-submit"
          disabled={!ready || busy}
          style={{
            minHeight: 44, borderRadius: 'var(--r-btn)', border: 0, fontFamily: 'inherit', fontWeight: 800, fontSize: 14,
            background: ready ? 'var(--accent)' : 'var(--surface-2)', color: ready ? 'var(--accent-on)' : 'var(--text-faint)',
            cursor: ready && !busy ? 'pointer' : 'default', opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? t('join.submitting') : t('join.submit')}
        </button>
        <p className="faint" style={{ fontSize: 11.5, margin: 0, textAlign: 'center' }}>{t('join.privacy')}</p>
      </form>
    </Card>
  )
}
